import { Show, createSignal, onCleanup, onMount, useContext } from 'solid-js';
import clsx from 'clsx';
import { ErrorCatcher } from '../../../tools/error-catcher';
import { ChevronDownIcon } from '../../icons-library/chevron-down';
import { PageContext } from '../../page/page-context-provider';
import { SidebarMenuDesktop } from '../desktop/sidebar-menu-desktop';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { Section } from '../../../grid-system/section/section';
import { MenuBarsIcon } from '../../icons-library/menu-bars';
import headerStore from '../../../stores/header-store';

// Mobile header height — must match `--spacing-cvi-header-h-mobile` (used as the sticky `top` offset).
const MOBILE_HEADER_OFFSET_PX = 72;

export const SidebarMenuMobile = () => {
    const [open, setOpen] = createSignal(false);
    const [isStuck, setIsStuck] = createSignal(false);
    const { sidebarMenu } = useContext(PageContext);
    const { localize } = useContext(AppContext);
    const { floatHeader: isHeaderCollapsed } = headerStore;

    let sentinelEl: HTMLDivElement | undefined;

    /* IntersectionObserver sentinel: sits 1px above the sticky sidebar in flow. The observer's
       root-margin is shifted up by the sticky offset, so the sentinel "leaves the viewport"
       exactly when the sidebar transitions from in-flow to stuck. While in flow → border-b
       visible; once stuck → border-b removed. */
    onMount(() => {
        if (!sentinelEl) return;
        const observer = new IntersectionObserver(
            ([entry]) => setIsStuck(!entry.isIntersecting),
            { rootMargin: `-${MOBILE_HEADER_OFFSET_PX}px 0px 0px 0px`, threshold: 0 }
        );
        observer.observe(sentinelEl);
        onCleanup(() => observer.disconnect());
    });

    return (
        <ErrorCatcher componentName="SidebarMenuMobile">
            <Show when={sidebarMenu()}>
                {/* Sentinel for the sticky-state detection — 1px tall, just above the sticky panel. */}
                <div ref={sentinelEl} aria-hidden="true" class="h-px tablet-original:hidden" />
                {/* Sticky just below the mobile/tablet header. Top offset comes from the
                    `cvi-header-h-mobile` token (72px) so this stays in sync if the header
                    height changes. Slides up + fades when isHeaderCollapsed flips on scroll-
                    down so both chrome surfaces hide together. */}
                <nav class={clsx(
                    '[container-type:inline-size] flex flex-col tablet-original:hidden border-t border-cvi-border-muted bg-cvi-bg-body z-[2] top-cvi-header-h-mobile overflow-x-clip',
                    'transition-[transform,opacity] duration-300 ease-in-out',
                    isHeaderCollapsed() && '-translate-y-full opacity-0',
                    // While closed: sticky in flow at top of viewport.
                    // While open: fixed to viewport at the same offset, full-width, with internal
                    //   scroll capped to viewport height — so the expanded menu is always visible
                    //   regardless of how deep the user is scrolled when they tap the toggle.
                    open()
                        ? 'fixed inset-x-0 max-h-[calc(100vh-var(--spacing-cvi-header-h-mobile))] overflow-y-auto'
                        : 'sticky',
                    // Border-b only when the panel is in its natural in-flow position. Once it
                    // becomes sticky-stuck, drop the divider.
                    !open() && !isStuck() && 'border-b'
                )}>
                    <Section
                        templateShorthand={[12]}
                        widthType="bgFull"
                        removeSpacingBlock={true}
                        customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
                    >
                        <button
                            class="group flex items-center gap-cvi-5 w-full py-cvi-6 bg-transparent border-0 text-left cursor-pointer"
                            onClick={() => setOpen(!open())}
                            aria-expanded={open()}
                            aria-label={open() ? localize('close-submenu', 'Close submenu') : localize('open-submenu', 'Open submenu')}
                        >
                            <span
                                class="flex items-center justify-center size-cvi-9 shrink-0 text-cvi-fg-default transition-transform duration-200"
                            >
                                <MenuBarsIcon ariaLabel={localize('toggle-submenu', 'Toggle submenu')} />
                            </span>
                            <span class="flex-1 min-w-0 heading-md font-cvi-medium text-cvi-fg-default">
                                {localize('menu', 'Menu')}
                            </span>
                            <span
                                class={clsx(
                                    'flex items-center justify-center size-cvi-9 shrink-0 text-cvi-fg-default transition-transform duration-200',
                                    open() && 'rotate-180'
                                )}
                            >
                                <ChevronDownIcon ariaLabel={localize('toggle-submenu', 'Toggle submenu')} />
                            </span>
                        </button>
                    </Section>

                    <Show when={open()}>
                        {/* Section keeps row content grid-aligned with the rest of the page;
                            the `mobile-panel` variant on SidebarMenuDesktop bleeds each row's
                            bg/border outward past the Section gutter so they reach the
                            viewport edges. */}
                        <Section
                            templateShorthand={[12]}
                            widthType="bgFull"
                            removeSpacingBlock={true}
                            customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
                        >
                            <div class="pb-cvi-9">
                                <SidebarMenuDesktop variant="mobile-panel" />
                            </div>
                        </Section>
                    </Show>
                </nav>
            </Show>
        </ErrorCatcher>
    );
};
